/**
 * La note du test — MÉCANIQUE uniquement, et le CHRONO de chaque étape.
 *
 * Décision de Kayla (18-08) : la machine ne note que ce qui est objectif ; ce
 * qu'il a écrit part tel quel sur sa fiche et c'est elle qui juge s'il donne
 * envie de payer. Aucun juge automatique.
 *
 * ─── CE QUE KAYLA VEUT TRAQUER (06-10-2026) ────────────────────────────────
 * « À la fin, on doit pouvoir traquer : combien de temps il a mis, est-ce qu'il
 * a bien réalisé le test, l'appareil, sa connexion. » L'appareil et la
 * connexion sont mesurés AVANT, par la vérif technique (`POST /device`,
 * `POST /probe`). Ce fichier porte les deux autres :
 *   · « a-t-il bien réalisé le test » → un critère par geste de la consigne
 *     (la file, le bon flow sur le bon fan, le flow laissé tranquille, le
 *     message à 50, la transaction, Reprendre) ;
 *   · « combien de temps » → la durée totale, celle de chaque étape (dans la
 *     `note` de son critère), et le temps passé sur l'introduction.
 *
 * 🪤 POURQUOI LE CHRONO EST AUSSI DANS LES NOTES, ET PAS SEULEMENT DANS LES
 * GESTES. Les marques début/fin partent dans `gestes` (type `debut`/`fin`) —
 * c'est la donnée brute. Mais la fiche admin ne reçoit pas encore `gestes`
 * (`_vente()` côté Cyrano ne le recopie pas, cf. le rapport du 06-10) ; les
 * `criteres`, eux, arrivent tels quels. Le temps de chaque étape écrit dans la
 * note de son critère est donc lisible AUJOURD'HUI, sans toucher au serveur.
 *
 * 🎯 ET LA STRATÉGIE (Kayla, 06-10-2026, le soir) : « l'idée qu'on doit savoir
 * quel média on vend […] lui faire penser que la vidéo a été faite en direct,
 * spécialement pour lui. On doit vendre cher. » Trois critères de plus —
 * `contenu` (il décrit le média), `pour_lui` (fait pour lui, en direct),
 * `valeur` (40 € au plus bas pour le même média, sinon un média plus petit) —
 * et une transaction jugée sur ce qui a été CONCLU, pas sur une fourchette.
 *
 * 🎥 ON VEND UN MÉDIA (Kayla, même soir) : plus de formule de l'ancienne
 * carte. La transaction se juge en trois morceaux — un produit VIDÉO qui vend
 * ce média (`tx_offre`), LE média dans « Délivrer au paiement » (`tx_media`,
 * séparé : c'est la faute la plus lisible — le fan paie et ne reçoit pas ce
 * qu'on lui a vendu), le prix conclu à l'euro près (`tx_prix`).
 *
 * 🪦 Les critères de la scène du 19-08 (`tarifs`, `offre`, `chauffe`) et
 * `tx_teaser` / `tx_livraison` sont partis le 06-10-2026. Les deux derniers
 * notaient des champs que le vrai composeur n'a plus : depuis le 13-09 la
 * transaction n'envoie que le lien (plus de légende, plus de teaser), et la
 * livraison y est « optionnelle ». Les fiches DÉJÀ passées ne bougent pas :
 * elles rendent le tableau qu'elles portent en base.
 */
import type { VenteGestePayload, VenteResultatPayload } from "../../types";
import type { Etat, Evenement, Geste, Marque } from "./moteur";
import {
  PLANCHER_DE_LA_VIDEO_CENTS,
  VIDEO_A_30,
  VIDEO_A_50,
  elementsDits,
  faitPourLui,
  mediaParId,
  offreParCle,
  type EtapeJouee,
} from "./scenario";

export interface Critere {
  cle: string;
  /** Ce qu'on regarde, en clair — affiché tel quel sur la fiche. */
  libelle: string;
  /** null = le candidat n'est jamais arrivé jusque-là (ou critère non noté). */
  reussi: boolean | null;
  /** Précision : ce qui a raté, et le temps de l'étape. */
  note?: string;
}

/** Ce que l'écran d'introduction a mesuré. */
export interface MesureDeLIntro {
  /** Temps passé sur l'intro, en ms. */
  ms: number;
  /** Jusqu'où il a regardé la vidéo, en % — null s'il ne l'a pas lancée. */
  videoPct: number | null;
}

export interface Resultat {
  criteres: Critere[];
  /** Nb de critères réussis / nb évalués. */
  reussis: number;
  total: number;
  /** Durée de la SIMULATION en secondes (intro exclue), null s'il n'a rien fait. */
  dureeSecondes: number | null;
  /** Tout ce qu'il a écrit, dans l'ordre — la matière que Kayla lit. */
  sesMessages: string[];
  /** Chaque geste avec son étape et sa provenance. */
  gestes: Geste[];
  /** Le début et la fin de chaque étape. */
  marques: Marque[];
  intro: MesureDeLIntro | null;
}

/** « 45 s », « 2 min 05 » — ce qu'on lit d'un coup d'œil sur la fiche. */
export function duree(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  return `${m} min ${String(s % 60).padStart(2, "0")}`;
}

function euros(cents: number): string {
  return `${(cents / 100).toFixed(0)} €`;
}

function evenementDe(evs: Evenement[], etape: EtapeJouee): Evenement | undefined {
  return evs.find((e) => e.etape === etape);
}

/** Le temps passé sur une étape, ou null si elle n'a jamais été ouverte. */
function tempsDe(marques: Marque[], etape: EtapeJouee): number | null {
  const m = marques.find((x) => x.etape === etape);
  return m && m.fin !== null ? m.fin - m.debut : null;
}

/** Une note faite de morceaux — les vides tombent, le chrono ferme la marche. */
function note(marques: Marque[], etape: EtapeJouee, ...morceaux: Array<string | null>): string | undefined {
  const t = tempsDe(marques, etape);
  const tout = [...morceaux.filter((m): m is string => !!m), ...(t === null ? [] : [duree(t)])];
  return tout.length ? tout.join(" · ") : undefined;
}

const LIBELLE_SOURCE: Record<string, string> = {
  script: "script",
  script_modifie: "script retouché",
  francis: "Francis",
  francis_modifie: "Francis retouché",
  ecrit: "écrit",
};

export function noter(etat: Etat, intro: MesureDeLIntro | null = null): Resultat {
  const evs = etat.evenements;
  const marques = etat.marques;
  const criteres: Critere[] = [];

  // ─── L'introduction : chronométrée, jamais notée. Il peut la survoler
  //     (Kayla : ne pas forcer la vidéo) — c'est une information, pas une faute.
  if (intro) {
    criteres.push({
      cle: "intro",
      libelle: "Temps passé sur l'introduction (non noté)",
      reussi: null,
      note: [
        duree(intro.ms),
        intro.videoPct === null ? "vidéo pas lancée" : `vidéo vue jusqu'à ${intro.videoPct} %`,
      ].join(" · "),
    });
  }

  // ─── 1. La file.
  const file = evenementDe(evs, "file");
  const fd = file?.detail;
  criteres.push({
    cle: "file",
    libelle: "Il ajoute les nouveaux fans à sa file",
    reussi: file ? file.reussi : null,
    note: fd
      ? note(
          marques,
          "file",
          `${fd["nouveaux"]}/${fd["sur"]} nouveaux`,
          Number(fd["anciens"]) > 0 ? `+ ${fd["anciens"]} ancien(s)` : null,
        )
      : undefined,
  });

  // ─── 2. Le flow : le BON flow sur un NOUVEAU fan.
  const flow = evenementDe(evs, "flow");
  const fl = flow?.detail;
  let noteFlow: string | undefined;
  if (flow && fl && fl["avance"] !== "forcee") {
    noteFlow = note(
      marques,
      "flow",
      fl["flow"] !== "script 1" ? `« ${fl["flow"]} » au lieu de « script 1 »` : null,
      Number(fl["nouveau"]) === 0 ? `sur ${fl["fan"]}, un ancien fan` : null,
      fl["via"] === "a_faire" ? "via « À faire maintenant »" : "via le menu Flow",
    );
  } else if (flow) {
    noteFlow = note(marques, "flow", "il a écrit au lieu de lancer le flow");
  }
  criteres.push({
    cle: "flow_lance",
    libelle: "Il lance le flow « script 1 » sur un nouveau fan",
    reussi: flow ? flow.reussi : null,
    note: noteFlow,
  });

  // ─── 3. Le flow se réalise — il n'écrit pas pendant.
  const tourne = evenementDe(evs, "flow_en_cours");
  const td = tourne?.detail;
  const ecrits = Number(td?.["ecrits"] ?? 0);
  criteres.push({
    cle: "flow_respecte",
    libelle: "Il laisse le flow finir sans écrire",
    reussi: tourne ? tourne.reussi : null,
    note: tourne
      ? note(
          marques,
          "flow_en_cours",
          ecrits > 0 ? `${ecrits} message${ecrits > 1 ? "s" : ""} pendant le flow` : null,
          td?.["arrete"] ? "il a arrêté le flow" : null,
        )
      : undefined,
  });

  // ─── 4. Le message à 50 — SA vidéo, à 50 €.
  const proposer = evenementDe(evs, "proposer");
  const pd = proposer?.detail;
  criteres.push({
    cle: "proposer",
    libelle: "Il propose sa vidéo à 50 €",
    reussi: proposer ? proposer.reussi : null,
    note: proposer
      ? note(
          marques,
          "proposer",
          pd?.["sansMessage"] ? "lien envoyé sans annoncer la vidéo" : null,
          pd?.["avance"] === "forcee" ? "le fan a fini par prendre la vidéo tout seul" : null,
          pd?.["source"] ? (LIBELLE_SOURCE[String(pd["source"])] ?? String(pd["source"])) : null,
        )
      : undefined,
  });

  // ─── 4 bis. La STRATÉGIE (Kayla, 06-10-2026) : connaître le média et le
  //     décrire, le faire sentir « fait pour lui », tenir la valeur.
  const venteOuverte = proposer !== undefined;
  const nego = evenementDe(evs, "negocier");
  const nd = nego?.detail;
  // Le contenu se juge sur la vidéo à 50, ou sur la plus petite si c'est
  // elle qu'il a vendue — on garde la lecture la plus favorable des deux.
  const dits50 = elementsDits(etat.messagesDeVente, VIDEO_A_50.id);
  const dits30 = etat.accord?.mediaId === VIDEO_A_30.id ? elementsDits(etat.messagesDeVente, VIDEO_A_30.id) : [];
  const dits = dits30.length > dits50.length ? dits30 : dits50;
  criteres.push({
    cle: "contenu",
    libelle: "Il dit ce qu'il y a dans la vidéo (au moins 2 éléments de sa fiche)",
    reussi: venteOuverte ? dits.length >= 2 : null,
    note: venteOuverte ? (dits.length ? dits.join(", ") : "il n'a rien dit du contenu") : undefined,
  });
  const passage = etat.messagesDeVente.map((m) => faitPourLui(m)).find((x) => x !== null) ?? null;
  criteres.push({
    cle: "pour_lui",
    libelle: "Il fait croire que la vidéo est faite pour lui, en direct",
    reussi: venteOuverte ? passage !== null : null,
    note: venteOuverte ? (passage ? `« ${passage} »` : "rien qui dise « fait pour toi »") : undefined,
  });
  let noteValeur: string | undefined;
  if (nego && nd) {
    const p = Number(nd["prixCents"] ?? 0);
    const issue = String(nd["issue"] ?? "");
    noteValeur = note(
      marques,
      "negocier",
      nd["avance"] === "forcee"
        ? "il n'a pas répondu à l'objection"
        : issue === "tenu" || (issue === "baisse" && p >= VIDEO_A_50.prixCents)
          ? `tenu à ${euros(p || VIDEO_A_50.prixCents)}`
          : issue === "baisse"
            ? `baissé à ${euros(p)} pour la même vidéo`
            : issue === "autre"
              ? `autre vidéo, plus petite : « ${VIDEO_A_30.titre} » à ${euros(p)}`
              : `il a bradé la vidéo proposée à 50 (${euros(p)})`,
      nd["sansReponse"] ? "réponse donnée par le lien de paiement" : null,
    );
  }
  criteres.push({
    cle: "valeur",
    libelle: "Il tient la valeur : 40 € au plus bas pour la même vidéo, sinon une vidéo plus petite",
    reussi: nego ? nego.reussi : null,
    note: noteValeur,
  });

  // ─── 5. La transaction : quatre critères, parce qu'on rate rarement tout
  //     ensemble et que le détail dit QUOI réapprendre. Elle doit être
  //     COHÉRENTE avec ce qui a été conclu : un produit VIDÉO qui vend ce
  //     média, LE média dans « Délivrer au paiement », le prix à l'euro près.
  //     Le juge principal est le média + le prix ; le produit vient après.
  const tx = evenementDe(evs, "transaction");
  const d = tx?.reussi ? tx.detail : undefined;
  const dejaPaye = etat.gestes.some((g) => g.type === "transaction" && g.texte.endsWith("déjà payé"));
  // Sans accord (le lien est parti avant toute annonce), on attend la vidéo à
  // 50, entre son plancher et 50 €.
  const accord = etat.accord;
  const attendu = mediaParId(accord?.mediaId ?? VIDEO_A_50.id)!;
  const LIBELLE_TX_OFFRE = "Le produit est un produit vidéo qui vend ce média";
  const LIBELLE_TX_MEDIA = "Il délivre au paiement LA vidéo conclue";
  const LIBELLE_TX_PRIX = `Le prix est celui conclu (la vidéo à 50 jamais sous ${euros(PLANCHER_DE_LA_VIDEO_CENTS)})`;
  if (!tx || !d) {
    criteres.push(
      {
        cle: "tx_envoyee",
        libelle: "Il envoie le lien de paiement (bouton $)",
        reussi: tx ? false : null,
        note: tx
          ? note(marques, "transaction", dejaPaye ? "« Il a déjà payé » coché — aucun lien n'est parti" : "aucun lien envoyé")
          : undefined,
      },
      { cle: "tx_offre", libelle: LIBELLE_TX_OFFRE, reussi: null },
      { cle: "tx_media", libelle: LIBELLE_TX_MEDIA, reussi: null },
      { cle: "tx_prix", libelle: LIBELLE_TX_PRIX, reussi: null },
    );
  } else {
    const prix = Number(d["prixCents"] ?? 0);
    const offre = offreParCle(String(d["offreCle"] ?? "") || null);
    const medias = String(d["medias"] ?? "").split(",").filter(Boolean);
    const bonProduit = offre !== null && attendu.produits.includes(offre.cle);
    const bonMedia = medias.includes(attendu.id);
    // La vidéo à 50 vendue sous son plancher : bradée, quel que soit l'accord.
    const videoBradee = medias.includes(VIDEO_A_50.id) && prix < PLANCHER_DE_LA_VIDEO_CENTS;
    const prixConclu = accord
      ? prix === accord.prixCents
      : prix >= PLANCHER_DE_LA_VIDEO_CENTS && prix <= VIDEO_A_50.prixCents;
    const nomsDelivres = medias.map((id) => mediaParId(id)?.titre ?? id);
    criteres.push(
      {
        cle: "tx_envoyee",
        libelle: "Il envoie le lien de paiement (bouton $)",
        reussi: true,
        note: note(
          marques,
          "transaction",
          d["via"] === "francis" ? "préparé par Francis" : null,
          dejaPaye ? "après un « Il a déjà payé » coché" : null,
        ),
      },
      {
        cle: "tx_offre",
        libelle: LIBELLE_TX_OFFRE,
        reussi: bonProduit,
        note: !offre
          ? "aucun produit — la vente n'a pas d'identité"
          : bonProduit
            ? offre.nom
            : `${offre.nom} — pour « ${attendu.titre} » : ${attendu.produits.map((c) => offreParCle(c)?.nom ?? c).join(" ou ")}`,
      },
      {
        cle: "tx_media",
        libelle: LIBELLE_TX_MEDIA,
        reussi: bonMedia,
        note: bonMedia
          ? attendu.titre
          : nomsDelivres.length
            ? `délivré : ${nomsDelivres.join(", ")} — conclu : « ${attendu.titre} »`
            : `rien à délivrer — le fan paie et ne reçoit pas « ${attendu.titre} »`,
      },
      {
        cle: "tx_prix",
        libelle: LIBELLE_TX_PRIX,
        reussi: prixConclu && !videoBradee,
        note: videoBradee
          ? `${euros(prix)} — la vidéo à 50 sous ${euros(PLANCHER_DE_LA_VIDEO_CENTS)}`
          : prixConclu
            ? `${euros(prix)}${d["devise"] && d["devise"] !== "EUR" ? ` (${d["devise"]})` : ""}`
            : accord
              ? `${euros(prix)} — conclu à ${euros(accord.prixCents)}`
              : `${euros(prix)} — hors de 40–50 €`,
      },
    );
  }

  // ─── 6. Reprendre.
  const rendre = evenementDe(evs, "rendre");
  criteres.push({
    cle: "rendre",
    libelle: "Il rend la main à Cyrano (Reprendre)",
    reussi: rendre ? rendre.reussi : null,
    note: rendre
      ? note(marques, "rendre", rendre.detail?.["avance"] === "forcee" ? "il n'a jamais touché Reprendre" : null)
      : undefined,
  });

  const evalues = criteres.filter((c) => c.reussi !== null);
  const premiere = marques[0];
  const derniere = marques[marques.length - 1];
  const dureeSecondes =
    premiere && derniere && derniere.fin !== null
      ? Math.max(0, Math.round((derniere.fin - premiere.debut) / 1000))
      : null;

  return {
    criteres,
    reussis: evalues.filter((c) => c.reussi === true).length,
    total: evalues.length,
    dureeSecondes,
    sesMessages: etat.sesMessages,
    gestes: etat.gestes,
    marques,
    intro,
  };
}

/* ── Les bornes du serveur ─────────────────────────────────────────────────
 * Recopiées de `VenteResultatBody` (Cyrano, `regie_qualification_public.py`) :
 * la route est publique et en `extra="forbid"`, donc un payload qui déborde
 * rend 422 — et le résultat d'un candidat qui a TOUT fait serait perdu. On
 * borne ICI plutôt que de le découvrir sur une fiche vide. */
const MAX_GESTES = 120;
const MAX_MESSAGES = 80;
const MAX_TEXTE = 2000;
const MAX_LIBELLE = 200;
const MAX_A_MS = 86_400_000;

function borneA(ms: number): number {
  return Math.min(MAX_A_MS, Math.max(0, Math.round(ms)));
}

/**
 * Les marques de temps, rangées comme des gestes : `debut` puis `fin` par
 * étape, et l'intro en tête (de 0 à sa durée). C'est ce qui permet de
 * rejouer le chronomètre à partir de la seule base, sans nouvelle colonne.
 */
function gestesDeTemps(r: Resultat): VenteGestePayload[] {
  const out: VenteGestePayload[] = [];
  if (r.intro) {
    out.push(
      { etape: "intro", type: "debut", texte: "", source: null, a_ms: 0, reussi: false },
      {
        etape: "intro",
        type: "fin",
        texte: r.intro.videoPct === null ? "vidéo pas lancée" : `vidéo ${r.intro.videoPct} %`,
        source: null,
        a_ms: borneA(r.intro.ms),
        reussi: true,
      },
    );
  }
  for (const m of r.marques) {
    out.push({ etape: m.etape, type: "debut", texte: "", source: null, a_ms: borneA(m.debut), reussi: false });
    if (m.fin !== null) {
      const critere = r.criteres.find((c) => c.cle === CRITERE_DE_L_ETAPE[m.etape]);
      out.push({
        etape: m.etape,
        type: "fin",
        texte: "",
        source: null,
        a_ms: borneA(m.fin),
        reussi: critere?.reussi === true,
      });
    }
  }
  return out;
}

/** Le critère qui porte le verdict de chaque étape. */
const CRITERE_DE_L_ETAPE: Record<EtapeJouee, string> = {
  file: "file",
  flow: "flow_lance",
  flow_en_cours: "flow_respecte",
  proposer: "proposer",
  negocier: "valeur",
  transaction: "tx_envoyee",
  rendre: "rendre",
};

/**
 * Le résultat, dans la forme que le backend range tel quel (`score_snapshot`).
 *
 * Une conversion explicite plutôt qu'un `Resultat` envoyé brut : `Resultat`
 * sert l'écran et peut changer librement ; le payload est écrit en base et
 * relu par la fiche admin des mois plus tard. ⚠️ Le CONTRAT ne change pas le
 * 06-10 : mêmes clés (`kind`, `criteres`, `reussis`, `total`,
 * `duree_secondes`, `ses_messages`, `gestes`), le serveur est en
 * `extra="forbid"` — le chrono passe DANS `gestes` (types `debut`/`fin`) et
 * dans les notes des critères, jamais dans un champ nouveau.
 */
export function versPayload(r: Resultat): VenteResultatPayload {
  const temps = gestesDeTemps(r);
  const siens: VenteGestePayload[] = r.gestes.map((g) => ({
    etape: g.etape,
    type: g.type,
    texte: g.texte.slice(0, MAX_TEXTE),
    source: g.source,
    a_ms: borneA(g.a),
    reussi: g.reussi,
  }));
  // Les marques de temps passent TOUTES (elles sont peu nombreuses et c'est le
  // chrono) ; ses gestes remplissent la place qui reste, dans l'ordre.
  const gestes = [...temps, ...siens.slice(0, Math.max(0, MAX_GESTES - temps.length))].sort(
    (x, y) => x.a_ms - y.a_ms,
  );
  return {
    kind: "vente",
    criteres: r.criteres.map((c) => ({
      cle: c.cle,
      libelle: c.libelle.slice(0, MAX_LIBELLE),
      reussi: c.reussi,
      ...(c.note === undefined ? {} : { note: c.note.slice(0, MAX_LIBELLE) }),
    })),
    reussis: r.reussis,
    total: r.total,
    duree_secondes: r.dureeSecondes === null ? null : Math.min(86_400, r.dureeSecondes),
    ses_messages: r.sesMessages.slice(0, MAX_MESSAGES).map((m) => m.slice(0, MAX_TEXTE)),
    gestes,
  };
}

